import {Button, Grid, MenuItem, Select, Stack, TextField, Typography} from "@mui/material";
import {useContext, useEffect, useState} from "react";
import {grey} from "@mui/material/colors";
import {HugeiconsIcon} from "@hugeicons/react";
import {Refresh01Icon, Upload01Icon} from "@hugeicons/core-free-icons";
import {Context as AppContext} from "../../../context/AppContext";
import {toast} from "react-toastify";
import {FlowProjectRepository} from "../../../repositories/FlowProjectRepository";
import axios from "axios";

const initialState = {
  chatbot_timeout: "",
  redirect_vdn: "",
  outbound_call: "",
  redirect_message: "",
  duration: "",
  silence: "",
  send_token: null,
  receive_token: null,
  max_calls_by_user: null,
  chatbot_api: "",
  conversation_logger_api: "",
  chatui_api: "",
  mcp_registry: "",
  health_check_api: "",
  knowledgebase_refresh_api: "",
  rag_url: "",
  neo4j_api: "",
  success_tag: null,
}

export const ProjectConfiguration = ({project}) => {
  const {updateProject} = useContext(AppContext);
  const [configuration, setConfiguration] = useState({...initialState});

  useEffect(() => {
    setConfiguration({...(project?.configuration || initialState)});
  }, [project?.configuration]);

  const onChange = e => {
    const key = e?.target?.name;
    const value = e?.target?.value;

    onUpdate(key, value === "none" ? null : value);
  }

  const onUpdate = (key, value) => {
    if (key) {
      setConfiguration({...configuration, [key]: value});
    }
  }

  const onSave = async () => {
    try {
      const newProject = {...project};
      newProject.configuration = {...configuration, max_calls_by_user: configuration?.max_calls_by_user ? +configuration?.max_calls_by_user : null};
      await updateProject(newProject);
      const fetchVersion = `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/api/apiProject/${project.id}`
      const projectVersionResponse = await axios.get(fetchVersion)
      await FlowProjectRepository.updateFlow({id: project?.id, project: projectVersionResponse?.data || null})
      toast.success("Project updated successfully")
    } catch (error) {
      console.log(error);
      toast.error("Failed to update project")
    }
  }

  const onRestore = () => {
    setConfiguration({...initialState, ...(project?.configuration || {})});
  }

  return (
    <Grid container maxWidth={"xl"} spacing={2} sx={{mx: "auto", p: {xs: 2, md: 4}, overflowY: "auto"}}>
      <Grid item xs={12}>
        <Typography fontWeight={700} fontSize={"1.05em"}>Redirect Message Properties</Typography>
        <Typography fontWeight={400} variant={"body2"} fontSize={"0.85em"}>This input configures chatbot interaction: {"'Chatbot Timeout'"} sets the wait time for a response, {"'VDN Number'"} defines the transfer destination if the chatbot fails to respond, and {"'Redirect Message'"} specifies the user notification when the timeout occurs</Typography>
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={3}>
        <Typography fontWeight={600} fontSize={"0.85em"} gutterBottom>Maximum Call Attempts Per User</Typography>
        <TextField
          value={configuration?.max_calls_by_user}
          placeholder={"3"}
          name={"max_calls_by_user"} type={"number"}
          helperText={"Maximum number of calls a user can make within 6 hours. When exceeded, calls are automatically redirected."}
          fullWidth
          onChange={onChange}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={6} lg={3}>
        <Typography fontWeight={600} fontSize={"0.85em"} gutterBottom>Chatbot Timeout</Typography>
        <TextField
          value={configuration?.chatbot_timeout}
          placeholder={"27 seconds"}
          name={"chatbot_timeout"} type={"number"}
          fullWidth
          onChange={onChange}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={6} lg={3}>
        <Typography fontWeight={600} fontSize={"0.85em"} gutterBottom>Redirect VDN Number</Typography>
        <TextField
          value={configuration?.redirect_vdn}
          placeholder={"Enter vdn number here"}
          name={"redirect_vdn"}
          fullWidth
          onChange={onChange}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={6} lg={3}>
        <Typography fontWeight={600} fontSize={"0.85em"} gutterBottom>Outbound Caller ID</Typography>
        <TextField
          value={configuration?.outbound_call}
          placeholder={"Enter outbound caller id number here"}
          helperText={"Caller ID used for Web Call / Phone Call originated from the Start node. Falls back to the Redirect VDN Number when left empty."}
          name={"outbound_call"}
          fullWidth
          onChange={onChange}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={6} lg={3}>
        <Typography fontWeight={600} fontSize={"0.85em"} gutterBottom>Success Tag</Typography>
        <Select
          fullWidth sx={{width: "100%"}}
          value={configuration?.success_tag || "none"}
          onChange={onChange}
          name={"success_tag"}
        >
          <MenuItem value={"none"}><em>None</em></MenuItem>
          {project?.settings?.tags?.map((tag) => (
            <MenuItem key={tag} value={tag?.id}>{tag?.name}</MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item xs={12} sm={6} md={12}>
        <Typography fontWeight={600} fontSize={"0.85em"} gutterBottom>Redirect Message</Typography>
        <TextField
          value={configuration?.redirect_message}
          placeholder={"We're sorry, but the chatbot is currently unavailable to respond. Please hold while we redirect your request."}
          name={"redirect_message"}
          fullWidth
          multiline rows={3}
          onChange={onChange}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography fontWeight={700} fontSize={"1.05em"}>Message Recording</Typography>
        <Typography fontWeight={400} variant={"body2"} fontSize={"0.85em"}>This field manages recording settings: {"'silence'"} defines the waiting time after the user stops speaking, while {"'duration'"} sets the maximum time a user can speak.</Typography>
      </Grid>
      <Grid item xs={12} sm={6} md={6}>
        <Typography fontWeight={600} fontSize={"0.85em"} gutterBottom>Recording Duration</Typography>
        <TextField
          value={configuration?.duration}
          placeholder={"14 seconds"}
          name={"duration"}
          type={"number"}
          onChange={onChange}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={6} md={6}>
        <Typography fontWeight={600} fontSize={"0.85em"} gutterBottom>Recording Silence</Typography>
        <TextField
          value={configuration?.silence}
          placeholder={"3 seconds"}
          name={"silence"}
          type={"number"}
          onChange={onChange}
          fullWidth
        />
      </Grid>

      <Grid item xs={12}>
        <Typography fontWeight={700} fontSize={"1.05em"}>Chatbot Communication</Typography>
        <Typography fontWeight={400} variant={"body2"} fontSize={"0.85em"}>
          This section configures chatbot communication. The {"'Send Message Token'"} is used to send messages to the chatbot,
          the {"'Receive Message Token'"} is for receiving responses, and the {"'Chatbot API'"} specifies the endpoint for communication.
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography fontWeight={600} fontSize={"0.85em"} gutterBottom>Conversation Logger API</Typography>
        <TextField
          value={configuration?.conversation_logger_api}
          placeholder={"Enter the conversation logger API endpoint..."}
          helperText="This API is used to log conversations — it stores the exchanged messages and related data."
          name={"conversation_logger_api"}
          onChange={onChange}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography fontWeight={600} fontSize={"0.85em"} gutterBottom>Chatbot API</Typography>
        <TextField
          value={configuration?.chatbot_api}
          placeholder={"Enter the chatbot API endpoint..."}
          helperText="This is the API endpoint where user messages are sent and the chatbot replies are received."
          name={"chatbot_api"}
          onChange={onChange}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography fontWeight={600} fontSize={"0.85em"} gutterBottom>ChatUI API</Typography>
        <TextField
          value={configuration?.chatui_api}
          placeholder={"Enter the chat ui API endpoint..."}
          helperText="This is the API endpoint where user messages are sent and the chatbot replies are received."
          name={"chatui_api"}
          onChange={onChange}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography fontWeight={600} fontSize={"0.85em"} gutterBottom>MCP Registry API</Typography>
        <TextField
          value={configuration?.mcp_registry}
          placeholder={"Enter the API endpoint for MCP registry..."}
          helperText="This API endpoint is used to check the chatbot's availability and responsiveness, ensuring it is functioning correctly."
          name={"mcp_registry"}
          onChange={onChange}
          fullWidth
        />
      </Grid>
      {/*<Grid item xs={12} sm={6}>*/}
      {/*  <Typography fontWeight={600} fontSize={"0.85em"} gutterBottom>Health Check Monitoring API</Typography>*/}
      {/*  <TextField*/}
      {/*    value={configuration?.health_check_api}*/}
      {/*    placeholder={"Enter the API endpoint used to monitor chatbot health..."}*/}
      {/*    helperText="This API endpoint is used to check the chatbot's availability and responsiveness, ensuring it is functioning correctly."*/}
      {/*    name={"health_check_api"}*/}
      {/*    onChange={onChange}*/}
      {/*    fullWidth*/}
      {/*  />*/}
      {/*</Grid>*/}
      <Grid item xs={12} sm={6}>
        <Typography fontWeight={600} fontSize={"0.85em"} gutterBottom>
          Initiate Call API
        </Typography>
        <TextField
          value={configuration?.initiate_call_api}
          placeholder={"Enter the API endpoint to initiate an outbound call..."}
          helperText="This API endpoint is used to trigger an outbound phone call from the agent to the customer."
          name={"initiate_call_api"}
          onChange={onChange}
          fullWidth
        />
      </Grid>

      <Grid item xs={12}>
        <Typography fontWeight={700} fontSize={"1.05em"}>Knowledge Base</Typography>
        <Typography fontWeight={400} variant={"body2"} fontSize={"0.85em"}>
          Knowledge base configurations
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography fontWeight={600} fontSize={"0.85em"} gutterBottom>
          Knowledge Base Refresh API
        </Typography>
        <TextField
          value={configuration?.knowledgebase_refresh_api}
          placeholder={"Enter the API endpoint to refresh knowledge base..."}
          helperText="This API endpoint is used to reload knowledge base."
          name={"knowledgebase_refresh_api"}
          onChange={onChange}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography fontWeight={600} fontSize={"0.85em"} gutterBottom>
          Rag URL
        </Typography>
        <TextField
          value={configuration?.rag_url}
          placeholder={"Enter the RAG URL endpoint"}
          name={"rag_url"}
          onChange={onChange}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography fontWeight={600} fontSize={"0.85em"} gutterBottom>
          Neo4j
        </Typography>
        <TextField
          value={configuration?.neo4j_api}
          placeholder={"Enter the Neo4j URL endpoint"}
          name={"neo4j_api"}
          onChange={onChange}
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <Stack direction={"row"} gap={1} justifyContent={"flex-end"}>
          <Button
            color={"secondary"} variant={"ghost"}
            startIcon={<HugeiconsIcon icon={Refresh01Icon} size={18} />} className={"border-shadow"}
            sx={{bgcolor: grey[100], px: 3, borderRadius: "8px !important", "&:hover": {bgcolor: grey[300],} }}
            onClick={onRestore}
          >
            Reset
          </Button>
          <Button
            color={"secondary"} variant={"modal"}
            startIcon={<HugeiconsIcon icon={Upload01Icon} size={18} />}
            sx={{borderRadius: "8px !important", px: 3}}
            onClick={onSave}
          >
            Save
          </Button>
        </Stack>
      </Grid>
    </Grid>
  )
}
